import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import SEO from '../components/SEO';
import LauncherSection from '../components/LauncherSection';
import NewsGrid from '../components/NewsGrid';
import useNews from '../hooks/useNews';

const Home = () => {
  const fadeIn = {
    initial: { opacity: 0, y: 20 },
    animate: (custom) => ({
      opacity: 1,
      y: 0,
      transition: { 
        delay: custom * 0.1,
        duration: 0.5 
      }
    })
  };

  // Sử dụng hook useNews để lấy dữ liệu tin tức
  const { news, loading } = useNews();

  return (
    <>
      <SEO 
        title="Trang chủ"
        description="Tham gia trải nghiệm thế giới Roleplay sống động, công việc phong phú, cộng đồng thân thiện và sự kiện hấp dẫn mỗi tuần!"
      />
      
      <Helmet>
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "WebPage",
              "name": "Trang chủ | SAMP/OPEN.MP - Chơi GTA San Andreas Multiplayer tại PSA Roleplay | PC & Mobile",
              "description": "Tham gia trải nghiệm thế giới Roleplay sống động, công việc phong phú, cộng đồng thân thiện và sự kiện hấp dẫn mỗi tuần!",
              "publisher": {
                "@type": "Organization",
                "name": "PSA Roleplay",
                "logo": {
                  "@type": "ImageObject",
                  "url": "https://psa.world/resources/images/logo_new.png"
                }
              }
            }
          `}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="min-h-screen flex items-center relative overflow-hidden" style={{
        backgroundImage: `url(${process.env.PUBLIC_URL}/resources/images/gta-bg.png)`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}>
        <div className="container mx-auto px-4 z-10 py-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <motion.h1 
                className="text-4xl md:text-6xl font-bold mb-6 text-white drop-shadow-sm font-montserrat"
                variants={fadeIn}
                initial="initial"
                animate="animate"
                custom={1}
              >
                BUILD YOUR PERSONAL LIFE IN VIRTUAL WORLD
              </motion.h1>
              
              <motion.p 
                className="text-xl mb-8 drop-shadow-text"
                variants={fadeIn}
                initial="initial"
                animate="animate"
                custom={2}
              >
                Bắt đầu hành trình xây dựng cuộc sống ảo của riêng bạn tại PSA Roleplay, một thế giới mở sống động nơi bạn có thể trở thành bất cứ ai bạn muốn. Tham gia vào các hoạt động đa dạng từ đua xe, làm việc, cùng các sự kiện hấp dẫn đang chờ đón bạn.
              </motion.p>
              
              <motion.div 
                className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4"
                variants={fadeIn}
                initial="initial"
                animate="animate"
                custom={3}
              >
                <Link to="/download" className="btn btn-primary neon-glow">
                  Bắt Đầu Ngay
                </Link>
                <Link to="/servers" className="btn btn-secondary neon-glow">
                  Danh sách máy chủ
                </Link>
              </motion.div>
              
              <motion.div 
                className="mt-12 text-light/80"
                variants={fadeIn}
                initial="initial"
                animate="animate"
                custom={4}
              >
                <p className="mb-2">Người Chơi Trực Tuyến: <span className="text-accent-teal">1,247</span></p>
                <div className="w-full bg-dark/50 h-2 rounded-full overflow-hidden">
                  <motion.div 
                    className="h-full gradient-teal"
                    initial={{ width: 0 }}
                    animate={{ width: '68%' }}
                    transition={{ delay: 0.5, duration: 1 }}
                  ></motion.div>
                </div>
                <p className="mt-2 text-sm">68% công suất máy chủ - Tham gia ngay!</p>
              </motion.div>
            </div>
            
            {/* Animated Character */}
            <motion.div
              className="relative hidden lg:block mt-12"
              initial={{ opacity: 0, x: 100 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ 
                duration: 0.7, 
                delay: 0.5,
                type: "spring",
                stiffness: 100
              }}
            >
              <motion.div 
                className="w-full max-w-xl mx-auto drop-shadow-2xl protected-image-container"
                animate={{ 
                  y: [0, -15, 0],
                }}
                transition={{
                  duration: 4,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              >
                <div 
                  className="protected-image"
                  style={{
                    backgroundImage: `url(${process.env.PUBLIC_URL}/resources/images/characters.png)`,
                    backgroundSize: 'contain',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                    width: '100%',
                    height: '700px'
                  }}
                ></div>
              </motion.div>
              
              {/* Light glow effect behind characters */}
              <motion.div
                className="absolute -inset-4 rounded-full bg-accent-teal/10 blur-xl -z-10"
                animate={{ 
                  opacity: [0.4, 0.8, 0.4],
                }}
                transition={{
                  duration: 4,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              />
              
              {/* Particle effects */}
              <ParticleEffect top="10%" left="20%" delay={0} zIndex={5} />
              <ParticleEffect top="30%" left="80%" delay={1} zIndex={5} />
              <ParticleEffect top="70%" left="15%" delay={2} zIndex={5} />
              <ParticleEffect top="50%" left="90%" delay={1.5} zIndex={5} />
              <ParticleEffect top="80%" left="50%" delay={0.5} zIndex={5} />
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* Phần còn lại của trang Home */}
      <div className="bg-dark-purple-main">
        {/* Tin Tức Section */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <NewsGrid news={loading ? [] : news} />
          </div>
        </section>
        
        {/* Subtle section divider */}
        <div className="container mx-auto px-4">
          <div className="section-divider"></div>
        </div>
        
        {/* Launcher Section */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <LauncherSection />
          </div>
        </section>
      </div>
    </>
  );
};

// Particle Animation Component
const ParticleEffect = ({ top, left, delay, zIndex = 2 }) => {
  return (
    <motion.div
      className="absolute w-2 h-2 rounded-full particle-effect"
      style={{ 
        top, 
        left,
        background: "linear-gradient(90deg, #ff7a95 0%, #ffb696 100%)",
        zIndex
      }}
      initial={{ opacity: 0, scale: 0 }}
      animate={{ 
        opacity: [0, 1, 0],
        scale: [0, 1.5, 0],
        y: [0, -20, 0]
      }}
      transition={{
        duration: 3,
        delay,
        repeat: Infinity,
        repeatDelay: Math.random() * 2
      }}
    />
  );
};

export default Home;

